import React from 'react';

import type { Text } from '@zillow/constellation';
import { AdornedInput, Adornment, Flex, FormField, FormHelp, Label, Paragraph } from '@zillow/constellation';
import { CurrencyInput, NumberStepper } from '@zillow/zrs-comet';

import type { HTMLElementEvent } from '../../../models/Types';
import type { Section } from '../../..//models';
import { getOrdinal } from '../../../utils/humanReadable';
import { BASE_RENT, LATE_RENT } from '../../../constants';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function LateRent_v4({ section, setSectionValues }: Props): JSX.Element {
  function onLateDaysChange(value: number): void {
    setSectionValues({
      [LATE_RENT.FIELD_NAME.LATE_DAYS]: value,
    });
  }

  function onLateFeeChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LATE_RENT.FIELD_NAME.FEE_AMOUNT]: value,
    });
  }

  const lateDays = section.getFieldValue(LATE_RENT.FIELD_NAME.LATE_DAYS);
  const rentDueDateField = section.getField(BASE_RENT.FIELD_NAME.DUE_DATE);

  return (
    <>
      <FormField
        label={<Label size="lg">When will a late fee be charged for past due rent?</Label>}
        control={
          <Flex display="flex" alignItems="center" flexDirection="column">
            <NumberStepper
              min={LATE_RENT.BOUNDS.MIN_DAYS}
              max={LATE_RENT.BOUNDS.MAX_DAYS}
              value={Number(lateDays)}
              onChange={onLateDaysChange}
            />
            <Paragraph>day(s) after the due date</Paragraph>
          </Flex>
        }
        description={
          <Flex display="flex" alignItems="center" flexDirection="column" marginY="xs">
            {rentDueDateField && rentDueDateField.isDefined() && (
              <FormHelp>
                Reminder: Rent is due the
                {` ${getOrdinal(section.getFieldValue(BASE_RENT.FIELD_NAME.DUE_DATE))} `}
                of every month.
              </FormHelp>
            )}
          </Flex>
        }
      />
      <FormField
        label={<Label>What is the late fee?</Label>}
        control={
          <AdornedInput
            leftAdornment={<Adornment>$</Adornment>}
            rightAdornment={<Adornment>flat fee</Adornment>}
            input={
              <CurrencyInput
                placeholder={CURRENCY_INPUT_PLACEHOLDER}
                value={section.getFieldValue(LATE_RENT.FIELD_NAME.FEE_AMOUNT)}
                onChange={onLateFeeChange}
                onBlur={onLateFeeChange}
              />
            }
            error={section.getField(LATE_RENT.FIELD_NAME.FEE_AMOUNT).hasError}
          />
        }
        description={
          section.getField(LATE_RENT.FIELD_NAME.FEE_AMOUNT).hasError && (
            <FormHelp error>{section.getField(LATE_RENT.FIELD_NAME.FEE_AMOUNT).getErrorMessage()}</FormHelp>
          )
        }
      />
    </>
  );
}
